import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import SantanderPixConfigPanel from '../components/SantanderPixConfigPanel';
import { 
  QrCode, Settings, Truck, Tag, CreditCard, BarChart3, 
  Plus, Trash2, Copy, Download, ExternalLink, CheckCircle,
  AlertCircle, Eye, EyeOff, Globe, MapPin, DollarSign, Percent,
  Loader2, Webhook, Link, RefreshCw, Clock, Power, Store
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function DigitalMenuPage() {
  const [activeTab, setActiveTab] = useState('config');
  const [loading, setLoading] = useState(true);
  
  // Config State
  const [config, setConfig] = useState(null);
  
  // QR Codes State
  const [qrCodes, setQrCodes] = useState([]);
  
  // Delivery Config State
  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [newNeighborhood, setNewNeighborhood] = useState({ neighborhood: '', fee_value: 0 });
  
  // Coupons State
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    name: '',
    coupon_type: 'PERCENTUAL',
    discount_value: 10,
    min_order_value: 0
  });
  
  // Payment Config State
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null); // 'testing' | 'success' | 'error' | null
  const [webhookTestStatus, setWebhookTestStatus] = useState(null);
  
  // Analytics State
  const [analytics, setAnalytics] = useState(null);

  // Store Status State (Funcionamento)
  const [storeStatus, setStoreStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [businessHours, setBusinessHours] = useState({});
  const [hoursLoading, setHoursLoading] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadConfig(),
      loadQRCodes(),
      loadDeliveryConfig(),
      loadNeighborhoods(),
      loadCoupons(),
      loadPaymentConfig(),
      loadAnalytics(),
      loadStoreStatus()
    ]);
    setLoading(false);
  };

  const loadConfig = async () => {
    try {
      const response = await api.get('/api/menu-admin/config');
      setConfig(response.data);
    } catch (err) {
      console.error('Error loading config:', err);
    }
  };

  const loadQRCodes = async () => {
    try {
      const response = await api.get('/api/menu-admin/qrcodes');
      setQrCodes(response.data);
    } catch (err) {
      console.error('Error loading QR codes:', err);
    }
  };

  const loadDeliveryConfig = async () => {
    try {
      const response = await api.get('/api/menu-admin/delivery/config');
      setDeliveryConfig(response.data);
    } catch (err) {
      console.error('Error loading delivery config:', err);
    }
  };

  const loadNeighborhoods = async () => {
    try {
      const response = await api.get('/api/menu-admin/delivery/neighborhoods');
      setNeighborhoods(response.data);
    } catch (err) {
      console.error('Error loading neighborhoods:', err);
    }
  };

  const loadCoupons = async () => {
    try {
      const response = await api.get('/api/menu-admin/coupons');
      setCoupons(response.data);
    } catch (err) {
      console.error('Error loading coupons:', err);
    }
  };

  const loadPaymentConfig = async () => {
    try {
      const response = await api.get('/api/menu-admin/payment/config');
      setPaymentConfig(response.data);
    } catch (err) {
      console.error('Error loading payment config:', err);
    }
  };

  const loadStoreStatus = async () => {
    try {
      const response = await api.get("/api/menu-admin/store-status");
      setStoreStatus(response.data);
      if (response.data.business_hours) {
        setBusinessHours(response.data.business_hours);
      }
    } catch (err) {
      console.error("Error loading store status:", err);
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/api/menu-admin/analytics/summary');
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error loading analytics:', err);
    }
  };

  const handleStoreStatus = async (action, reason = null) => {
    setStatusLoading(true);
    try {
      const response = await api.post('/api/menu-admin/store-status', { action, reason });
      setStoreStatus(response.data);
      const labels = { AUTO: 'Modo automatico ativado', OPEN: 'Loja aberta manualmente', CLOSED: 'Loja fechada manualmente' };
      toast.success(labels[action] || 'Status atualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atualizar status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleSaveBusinessHours = async () => {
    setHoursLoading(true);
    try {
      await api.put('/api/menu-admin/config', { business_hours_json: JSON.stringify(businessHours) });
      toast.success('Horarios salvos com sucesso');
      await loadStoreStatus();
    } catch (err) {
      toast.error('Erro ao salvar horarios');
    } finally {
      setHoursLoading(false);
    }
  };

  const updateDaySlots = (day, slots) => {
    setBusinessHours(prev => ({ ...prev, [day]: slots }));
  };

  const addSlot = (day) => {
    const current = businessHours[day] || [];
    updateDaySlots(day, [...current, { open: '11:30', close: '23:00' }]);
  };

  const removeSlot = (day, idx) => {
    const current = businessHours[day] || [];
    updateDaySlots(day, current.filter((_, i) => i !== idx));
  };

  const updateSlot = (day, idx, field, value) => {
    const current = businessHours[day] || [];
    const updated = current.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    updateDaySlots(day, updated);
  };


  const saveConfig = async (updates) => {
    try {
      await api.put('/api/menu-admin/config', updates);
      await loadConfig();
      toast.success('Configuração salva');
    } catch (err) {
      toast.error('Erro ao salvar configuração');
    }
  };

  const publishMenu = async () => {
    try {
      await api.post('/api/menu-admin/config/publish');
      await loadConfig();
      toast.success('Cardápio publicado!');
    } catch (err) {
      toast.error('Erro ao publicar cardápio');
    }
  };

  const unpublishMenu = async () => {
    try {
      await api.post('/api/menu-admin/config/unpublish');
      await loadConfig();
      toast.success('Cardápio despublicado');
    } catch (err) {
      toast.error('Erro ao despublicar cardápio');
    }
  };

  const createQRCode = async () => {
    try {
      await api.post('/api/menu-admin/qrcodes', {
        qr_type: 'STORE',
        reference_name: 'QR Code Principal'
      });
      await loadQRCodes();
      toast.success('QR Code criado');
    } catch (err) {
      toast.error('Erro ao criar QR Code');
    }
  };

  const deleteQRCode = async (id) => {
    try {
      await api.delete(`/api/menu-admin/qrcodes/${id}`);
      await loadQRCodes();
      toast.success('QR Code removido');
    } catch (err) {
      toast.error('Erro ao remover QR Code');
    }
  };

  const saveDeliveryConfig = async () => {
    try {
      let originLat = deliveryConfig?.origin_lat;
      let originLng = deliveryConfig?.origin_lng;
      
      // Auto-geocodificar endereço de origem se mudou ou não tem coordenadas
      if (deliveryConfig?.origin_address && (!originLat || !originLng)) {
        try {
          toast.info('Buscando coordenadas da origem...');
          const geoResp = await fetch(
            `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(deliveryConfig.origin_address + ", Brasil")}&limit=1&countrycodes=br`,
            { headers: { 'User-Agent': 'HitGrill/1.0' } }
          );
          const geoData = await geoResp.json();
          if (geoData.length > 0) {
            originLat = parseFloat(geoData[0].lat);
            originLng = parseFloat(geoData[0].lon);
            setDeliveryConfig(prev => ({ ...prev, origin_lat: originLat, origin_lng: originLng }));
            toast.success(`Coordenadas: ${originLat.toFixed(5)}, ${originLng.toFixed(5)}`);
          } else {
            toast.error('Não foi possível geocodificar o endereço de origem');
            return;
          }
        } catch (e) {
          console.error('Geocoding error:', e);
          toast.error('Erro ao buscar coordenadas');
          return;
        }
      }
      
      const payload = {
        base_fee: parseFloat(deliveryConfig?.base_fee) || 0,
        fee_per_km: parseFloat(deliveryConfig?.fee_per_km) || 0,
        free_km: parseFloat(deliveryConfig?.free_km) || 0,
        max_distance_km: parseFloat(deliveryConfig?.max_distance_km) || 15,
        free_delivery_min_order: deliveryConfig?.free_delivery_min_order ? parseFloat(deliveryConfig.free_delivery_min_order) : null,
        origin_lat: originLat || null,
        origin_lng: originLng || null,
        origin_address: deliveryConfig?.origin_address || null,
      };
      console.log('Saving delivery config:', payload);
      await api.put('/api/menu-admin/delivery/config', payload);
      toast.success('Configuração de frete salva');
    } catch (err) {
      console.error('Error saving delivery config:', err);
      toast.error('Erro ao salvar configuração de frete');
    }
  };

  const addNeighborhood = async () => {
    if (!newNeighborhood.neighborhood) {
      toast.error('Informe o nome do bairro');
      return;
    }
    try {
      await api.post('/api/menu-admin/delivery/neighborhoods', newNeighborhood);
      await loadNeighborhoods();
      setNewNeighborhood({ neighborhood: '', fee_value: 0 });
      toast.success('Bairro adicionado');
    } catch (err) {
      toast.error('Erro ao adicionar bairro');
    }
  };

  const deleteNeighborhood = async (id) => {
    try {
      await api.delete(`/api/menu-admin/delivery/neighborhoods/${id}`);
      await loadNeighborhoods();
      toast.success('Bairro removido');
    } catch (err) {
      toast.error('Erro ao remover bairro');
    }
  };

  const createCoupon = async () => {
    if (!newCoupon.code || !newCoupon.name) {
      toast.error('Preencha código e nome do cupom');
      return;
    }
    try {
      await api.post('/api/menu-admin/coupons', newCoupon);
      await loadCoupons();
      setNewCoupon({ code: '', name: '', coupon_type: 'PERCENTUAL', discount_value: 10, min_order_value: 0 });
      toast.success('Cupom criado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar cupom');
    }
  };

  const toggleCoupon = async (id, isActive) => {
    try {
      await api.put(`/api/menu-admin/coupons/${id}`, { is_active: !isActive });
      await loadCoupons();
      toast.success(isActive ? 'Cupom desativado' : 'Cupom ativado');
    } catch (err) {
      toast.error('Erro ao atualizar cupom');
    }
  };

  const savePaymentConfig = async () => {
    try {
      await api.put('/api/menu-admin/payment/config', paymentConfig);
      await loadPaymentConfig();
      toast.success('Configuração de pagamento salva');
    } catch (err) {
      toast.error('Erro ao salvar configuração de pagamento');
    }
  };

  const testPaymentConnection = async () => {
    setConnectionStatus('testing');
    try {
      const response = await api.post('/api/menu-admin/payment/test');
      if (response.data.success) {
        setConnectionStatus('success');
        toast.success(response.data.message);
        await loadPaymentConfig();
      } else {
        setConnectionStatus('error');
        toast.error(response.data.message);
      }
    } catch (err) {
      setConnectionStatus('error');
      toast.error('Erro ao testar conexão');
    }
  };

  const testWebhook = async () => {
    setWebhookTestStatus('testing');
    try {
      // Enviar teste de webhook
      const response = await fetch(`${API_URL}/api/mercadopago/webhook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: 'test', id: 'test-' + Date.now() })
      });
      const data = await response.json();
      if (data.status === 'ok') {
        setWebhookTestStatus('success');
        toast.success('Webhook funcionando! Endpoint respondeu corretamente.');
      } else {
        setWebhookTestStatus('error');
        toast.error('Webhook retornou erro');
      }
    } catch (err) {
      setWebhookTestStatus('error');
      toast.error('Erro ao testar webhook');
    }
  };

  const getWebhookUrl = () => {
    // Gerar URL do webhook baseado no domínio atual
    const baseUrl = window.location.origin;
    return `${baseUrl}/api/mercadopago/webhook`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado!');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
            <p className="mt-4 text-slate-600">Carregando...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Cardápio Digital</h1>
            <p className="text-slate-500">Configure seu cardápio online e QRpedir</p>
          </div>
          
          <div className="flex items-center gap-3">
            {config?.status === 'PUBLISHED' ? (
              <>
                <Badge className="bg-green-100 text-green-700">
                  <Globe className="w-4 h-4 mr-1" />
                  Publicado
                </Badge>
                <Button variant="outline" onClick={unpublishMenu}>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Despublicar
                </Button>
              </>
            ) : (
              <>
                <Badge variant="secondary">
                  Rascunho
                </Badge>
                <Button onClick={publishMenu}>
                  <Eye className="w-4 h-4 mr-2" />
                  Publicar Cardápio
                </Button>
              </>
            )}
            
            {config?.menu_url && (
              <Button variant="outline" asChild>
                <a href={config.menu_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Ver Cardápio
                </a>
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full max-w-2xl">
            <TabsTrigger value="config" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Configurações
            </TabsTrigger>
            <TabsTrigger value="qrcodes" className="flex items-center gap-2">
              <QrCode className="w-4 h-4" />
              QR Codes
            </TabsTrigger>
            <TabsTrigger value="delivery" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Frete
            </TabsTrigger>
            <TabsTrigger value="coupons" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Cupons
            </TabsTrigger>
            <TabsTrigger value="payment" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Pagamento
            </TabsTrigger>
            <TabsTrigger value="funcionamento" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Horarios
            </TabsTrigger>
          </TabsList>

          {/* Config Tab */}
          <TabsContent value="config" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Configurações Gerais</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>URL do Cardápio</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input value={`/menu/${config?.store_slug}`} readOnly />
                      <Button variant="outline" size="icon" onClick={() => copyToClipboard(`${window.location.origin}/menu/${config?.store_slug}`)}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <Label>Valor Mínimo do Pedido</Label>
                    <Input 
                      type="number"
                      value={config?.min_order_value || 0}
                      onChange={(e) => saveConfig({ min_order_value: parseFloat(e.target.value) })}
                    />
                  </div>
                  
                  <Separator />
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Mostrar Preços</Label>
                      <Switch 
                        checked={config?.show_prices}
                        onCheckedChange={(checked) => saveConfig({ show_prices: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Permitir Pedidos</Label>
                      <Switch 
                        checked={config?.allow_order}
                        onCheckedChange={(checked) => saveConfig({ allow_order: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Delivery</Label>
                      <Switch 
                        checked={config?.delivery_enabled}
                        onCheckedChange={(checked) => saveConfig({ delivery_enabled: checked })}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Retirada no Local</Label>
                      <Switch 
                        checked={config?.takeout_enabled}
                        onCheckedChange={(checked) => saveConfig({ takeout_enabled: checked })}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Estatísticas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-800">{analytics?.total_views || 0}</p>
                      <p className="text-sm text-slate-500">Visualizações</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-800">{analytics?.total_orders || 0}</p>
                      <p className="text-sm text-slate-500">Pedidos</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-slate-800">{analytics?.events?.add_to_cart || 0}</p>
                      <p className="text-sm text-slate-500">Add ao Carrinho</p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{analytics?.conversion_rate || 0}%</p>
                      <p className="text-sm text-slate-500">Conversão</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* QR Codes Tab */}
          <TabsContent value="qrcodes" className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>QR Codes</CardTitle>
                  <CardDescription>Gerencie os QR Codes do seu cardápio</CardDescription>
                </div>
                <Button onClick={createQRCode}>
                  <Plus className="w-4 h-4 mr-2" />
                  Novo QR Code
                </Button>
              </CardHeader>
              <CardContent>
                {qrCodes.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <QrCode className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhum QR Code criado</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {qrCodes.map((qr) => (
                      <Card key={qr.id} className="overflow-hidden">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-medium">{qr.reference_name || 'QR Code'}</p>
                              <Badge variant="secondary" className="mt-1 text-xs">{qr.qr_type}</Badge>
                            </div>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteQRCode(qr.id)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="bg-slate-100 p-4 rounded-lg flex items-center justify-center mb-3">
                            <QrCode className="w-24 h-24 text-slate-600" />
                          </div>
                          
                          <div className="text-sm text-slate-500 space-y-1">
                            <p>Scans: {qr.scan_count}</p>
                          </div>
                          
                          <div className="flex gap-2 mt-3">
                            <Button variant="outline" size="sm" className="flex-1" onClick={() => copyToClipboard(`${window.location.origin}${qr.full_url}`)}>
                              <Copy className="w-4 h-4 mr-1" />
                              Copiar
                            </Button>
                            <Button variant="outline" size="sm" className="flex-1">
                              <Download className="w-4 h-4 mr-1" />
                              Baixar
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Delivery Tab */}
          <TabsContent value="delivery" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Taxa por Quilômetro</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Taxa Base (R$)</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={deliveryConfig?.base_fee || 0}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, base_fee: parseFloat(e.target.value)})}
                    />
                  </div>
                  
                  <div>
                    <Label>Valor por KM (R$)</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={deliveryConfig?.fee_per_km || 0}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, fee_per_km: parseFloat(e.target.value)})}
                    />
                  </div>
                  
                  <div>
                    <Label>KM Gratuitos</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={deliveryConfig?.free_km || 0}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, free_km: parseFloat(e.target.value)})}
                    />
                  </div>
                  
                  <div>
                    <Label>Distância Máxima (KM)</Label>
                    <Input 
                      type="number"
                      step="0.5"
                      value={deliveryConfig?.max_distance_km || 0}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, max_distance_km: parseFloat(e.target.value)})}
                    />
                  </div>
                  
                  <div>
                    <Label>Frete Grátis Acima de (R$)</Label>
                    <Input 
                      type="number"
                      step="1"
                      value={deliveryConfig?.free_delivery_min_order || ''}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, free_delivery_min_order: parseFloat(e.target.value) || null})}
                      placeholder="Deixe em branco para desabilitar"
                    />
                  </div>
                  
                  <div className="border-t pt-4 mt-4">
                    <Label className="font-semibold">Endereço de Origem (Loja)</Label>
                    <p className="text-xs text-slate-500 mb-2">
                      Usado para calcular a distância real até o cliente
                    </p>
                    <Input 
                      placeholder="Ex: Av. Hildebrando de Lima, 699, Osasco, SP"
                      value={deliveryConfig?.origin_address || ''}
                      onChange={(e) => setDeliveryConfig({...deliveryConfig, origin_address: e.target.value})}
                    />
                    {deliveryConfig?.origin_lat && deliveryConfig?.origin_lng ? (
                      <p className="text-xs text-green-600 mt-1">
                        Coordenadas: {Number(deliveryConfig.origin_lat).toFixed(5)}, {Number(deliveryConfig.origin_lng).toFixed(5)}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-1">
                        Sem coordenadas. Salve para geocodificar automaticamente.
                      </p>
                    )}
                  </div>
                  
                  <Button onClick={saveDeliveryConfig} className="w-full">
                    Salvar Configurações
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>Taxa por Bairro</CardTitle>
                    <CardDescription>Configure taxas específicas por bairro</CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="Nome do bairro"
                      value={newNeighborhood.neighborhood}
                      onChange={(e) => setNewNeighborhood({...newNeighborhood, neighborhood: e.target.value})}
                    />
                    <Input 
                      type="number"
                      placeholder="Taxa"
                      className="w-24"
                      value={newNeighborhood.fee_value}
                      onChange={(e) => setNewNeighborhood({...newNeighborhood, fee_value: parseFloat(e.target.value)})}
                    />
                    <Button onClick={addNeighborhood}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {neighborhoods.map((n) => (
                      <div key={n.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-slate-400" />
                          <span>{n.neighborhood}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{formatCurrency(n.fee_value)}</Badge>
                          <Button variant="ghost" size="icon" className="text-red-500" onClick={() => deleteNeighborhood(n.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Coupons Tab */}
          <TabsContent value="coupons" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Criar Novo Cupom</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <Input 
                    placeholder="Código (ex: PROMO10)"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                  />
                  <Input 
                    placeholder="Nome do cupom"
                    value={newCoupon.name}
                    onChange={(e) => setNewCoupon({...newCoupon, name: e.target.value})}
                  />
                  <select 
                    className="border rounded-md px-3"
                    value={newCoupon.coupon_type}
                    onChange={(e) => setNewCoupon({...newCoupon, coupon_type: e.target.value})}
                  >
                    <option value="PERCENTUAL">Percentual (%)</option>
                    <option value="VALOR_FIXO">Valor Fixo (R$)</option>
                    <option value="FRETE_GRATIS">Frete Grátis</option>
                  </select>
                  <Input 
                    type="number"
                    placeholder="Valor desconto"
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon({...newCoupon, discount_value: parseFloat(e.target.value)})}
                  />
                  <Button onClick={createCoupon}>
                    <Plus className="w-4 h-4 mr-2" />
                    Criar
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Cupons Ativos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {coupons.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Tag className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhum cupom criado</p>
                    </div>
                  ) : (
                    coupons.map((coupon) => (
                      <div key={coupon.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="p-2 bg-white rounded-lg">
                            {coupon.coupon_type === 'PERCENTUAL' ? (
                              <Percent className="w-6 h-6 text-orange-500" />
                            ) : coupon.coupon_type === 'FRETE_GRATIS' ? (
                              <Truck className="w-6 h-6 text-green-500" />
                            ) : (
                              <DollarSign className="w-6 h-6 text-blue-500" />
                            )}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-bold">{coupon.code}</p>
                              <Badge variant={coupon.is_active ? "default" : "secondary"}>
                                {coupon.is_active ? 'Ativo' : 'Inativo'}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-500">{coupon.name}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-bold text-lg">
                              {coupon.coupon_type === 'PERCENTUAL' 
                                ? `${coupon.discount_value}%`
                                : coupon.coupon_type === 'FRETE_GRATIS'
                                ? 'Frete Grátis'
                                : formatCurrency(coupon.discount_value)
                              }
                            </p>
                            <p className="text-xs text-slate-500">
                              Usado: {coupon.usage_count}{coupon.usage_limit ? `/${coupon.usage_limit}` : ''} vezes
                            </p>
                          </div>
                          
                          <Switch 
                            checked={coupon.is_active}
                            onCheckedChange={() => toggleCoupon(coupon.id, coupon.is_active)}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Configuração de Pagamento Online</CardTitle>
                <CardDescription>Configure seu gateway de pagamento para receber pagamentos online</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {paymentConfig?.is_configured ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle className="w-5 h-5" />
                      <p className="font-medium">Pagamento Online - Configurado</p>
                    </div>
                    <p className="text-sm text-green-600 mt-1">
                      Integração com MercadoPago ativa. Ambiente: {paymentConfig?.environment === 'sandbox' ? 'Testes' : 'Produção'}
                    </p>
                  </div>
                ) : (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-center gap-2 text-amber-700">
                      <AlertCircle className="w-5 h-5" />
                      <p className="font-medium">Pagamento Online - Não Configurado</p>
                    </div>
                    <p className="text-sm text-amber-600 mt-1">
                      Configure suas credenciais do MercadoPago para ativar pagamentos online.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label>Provedor</Label>
                      <select 
                        className="w-full border rounded-md px-3 py-2 mt-1"
                        value={paymentConfig?.provider || 'MERCADOPAGO'}
                        onChange={(e) => setPaymentConfig({...paymentConfig, provider: e.target.value})}
                      >
                        <option value="MERCADOPAGO">MercadoPago</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label>Ambiente</Label>
                      <select 
                        className="w-full border rounded-md px-3 py-2 mt-1"
                        value={paymentConfig?.environment || 'sandbox'}
                        onChange={(e) => setPaymentConfig({...paymentConfig, environment: e.target.value})}
                      >
                        <option value="sandbox">Sandbox (Testes)</option>
                        <option value="production">Produção</option>
                      </select>
                    </div>
                    
                    <div>
                      <Label>Public Key</Label>
                      <Input 
                        placeholder="TEST-xxx ou APP_USR-xxx"
                        value={paymentConfig?.public_key || ''}
                        onChange={(e) => setPaymentConfig({...paymentConfig, public_key: e.target.value})}
                      />
                      <p className="text-xs text-slate-500 mt-1">Chave pública do MercadoPago</p>
                    </div>
                    
                    <div>
                      <Label>Access Token</Label>
                      <Input 
                        type="password"
                        placeholder="TEST-xxx ou APP_USR-xxx"
                        value={paymentConfig?.access_token || paymentConfig?.access_token_masked || ''}
                        onChange={(e) => setPaymentConfig({...paymentConfig, access_token: e.target.value})}
                      />
                      <p className="text-xs text-slate-500 mt-1">Token de acesso (credencial privada)</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>PIX</Label>
                      <Switch 
                        checked={paymentConfig?.pix_enabled}
                        onCheckedChange={(checked) => setPaymentConfig({...paymentConfig, pix_enabled: checked})}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Cartão de Crédito</Label>
                      <Switch 
                        checked={paymentConfig?.credit_card_enabled}
                        onCheckedChange={(checked) => setPaymentConfig({...paymentConfig, credit_card_enabled: checked})}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <Label>Cartão de Débito</Label>
                      <Switch 
                        checked={paymentConfig?.debit_card_enabled}
                        onCheckedChange={(checked) => setPaymentConfig({...paymentConfig, debit_card_enabled: checked})}
                      />
                    </div>
                    
                    <div>
                      <Label>Máximo de Parcelas</Label>
                      <Input 
                        type="number"
                        value={paymentConfig?.max_installments || 12}
                        onChange={(e) => setPaymentConfig({...paymentConfig, max_installments: parseInt(e.target.value)})}
                      />
                    </div>
                    
                    <div className="pt-4">
                      <div className="flex items-center justify-between">
                        <Label>Status</Label>
                        <Badge variant={paymentConfig?.is_configured ? "default" : "secondary"}>
                          {paymentConfig?.is_configured ? 'Configurado' : 'Não configurado'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button onClick={savePaymentConfig}>
                    Salvar Configurações
                  </Button>
                  <Button variant="outline" onClick={testPaymentConnection}>
                    Testar Conexão
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Painel Santander PIX */}
            <Card>
              <CardHeader>
                <CardTitle>PIX Santander (alternativo ao Mercado Pago)</CardTitle>
                <CardDescription>
                  Configure a integração PIX com o Santander. Ativando aqui, novos pedidos usam Santander em vez de MP.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SantanderPixConfigPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Funcionamento Tab */}
          <TabsContent value="funcionamento" className="space-y-6">
            {/* Status Operacional */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Power className="w-5 h-5" />
                  Status Operacional
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg border" style={{
                  backgroundColor: storeStatus?.is_open ? '#f0fdf4' : '#fef2f2',
                  borderColor: storeStatus?.is_open ? '#86efac' : '#fca5a5'
                }}>
                  <div className={`w-4 h-4 rounded-full ${storeStatus?.is_open ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
                  <div>
                    <p className="font-semibold text-lg">
                      {storeStatus?.is_open ? 'Loja Aberta' : 'Loja Fechada'}
                    </p>
                    <p className="text-sm text-slate-600">
                      {storeStatus?.override === 'MANUAL_OPEN' && 'Aberta manualmente'}
                      {storeStatus?.override === 'MANUAL_CLOSED' && `Fechada manualmente${storeStatus?.manual_status_reason ? ': ' + storeStatus.manual_status_reason : ''}`}
                      {storeStatus?.override === 'AUTO' && 'Modo automatico (agenda de horarios)'}
                      {!storeStatus?.override && 'Carregando...'}
                    </p>
                    {storeStatus?.manual_status_changed_by && storeStatus?.override !== 'AUTO' && (
                      <p className="text-xs text-slate-400 mt-1">
                        Alterado por: {storeStatus.manual_status_changed_by}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <Button
                    variant={storeStatus?.manual_status === 'AUTO' ? 'default' : 'outline'}
                    onClick={() => handleStoreStatus('AUTO')}
                    disabled={statusLoading}
                    className="flex items-center gap-2"
                    data-testid="store-status-auto-btn"
                  >
                    <Clock className="w-4 h-4" />
                    Automatico
                  </Button>
                  <Button
                    variant={storeStatus?.manual_status === 'OPEN' ? 'default' : 'outline'}
                    onClick={() => handleStoreStatus('OPEN')}
                    disabled={statusLoading}
                    className="flex items-center gap-2 border-green-300 hover:bg-green-50"
                    data-testid="store-status-open-btn"
                  >
                    <Store className="w-4 h-4 text-green-600" />
                    Abrir Agora
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      const reason = prompt('Motivo do fechamento (opcional):');
                      handleStoreStatus('CLOSED', reason || null);
                    }}
                    disabled={statusLoading}
                    className="flex items-center gap-2 border-red-300 hover:bg-red-50"
                    data-testid="store-status-close-btn"
                  >
                    <Power className="w-4 h-4 text-red-600" />
                    Fechar Agora
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Horarios de Funcionamento */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  Horarios de Funcionamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(day => {
                  const labels = {monday:'Segunda',tuesday:'Terca',wednesday:'Quarta',thursday:'Quinta',friday:'Sexta',saturday:'Sabado',sunday:'Domingo'};
                  const slots = businessHours[day] || [];
                  return (
                    <div key={day} className="flex items-start gap-4 p-3 rounded border bg-slate-50">
                      <div className="w-24 pt-1 font-medium text-sm">{labels[day]}</div>
                      <div className="flex-1 space-y-2">
                        {slots.length === 0 && <span className="text-sm text-slate-400">Fechado</span>}
                        {slots.map((slot, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={slot.open}
                              onChange={(e) => updateSlot(day, idx, 'open', e.target.value)}
                              className="w-32 h-8 text-sm"
                            />
                            <span className="text-sm text-slate-400">ate</span>
                            <Input
                              type="time"
                              value={slot.close}
                              onChange={(e) => updateSlot(day, idx, 'close', e.target.value)}
                              className="w-32 h-8 text-sm"
                            />
                            <Button variant="ghost" size="sm" onClick={() => removeSlot(day, idx)} className="h-8 px-2 text-red-500 hover:text-red-700">
                              X
                            </Button>
                          </div>
                        ))}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => addSlot(day)} className="h-8 text-xs">
                        + Turno
                      </Button>
                    </div>
                  );
                })}
                <div className="pt-3 flex justify-end">
                  <Button onClick={handleSaveBusinessHours} disabled={hoursLoading} data-testid="save-hours-btn">
                    {hoursLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando...</> : 'Salvar Horarios'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}

export default DigitalMenuPage;
